import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { firstValueFrom } from 'rxjs';
import { environment } from '@env';
import { type CurrentUser, type UserRole } from '../models/user.model';
import { TableStateService } from '../services/table-state.service';
import { STORAGE_KEYS } from '../constants/storage-keys';
import { decodeJwtPayload } from '../utils/jwt';

// Fallback: claim в JWT (если когда-нибудь появится). Основной источник —
// `GET /identity/me` после логина, см. enrichUserFromIdentityApi.
const ALLOWED_COMPANIES_CLAIM_KEYS = [
  'companies',
  'companyIds',
  'allowed_companies',
  'allowedCompanyIds',
] as const;

interface BackendMeResponse {
  keycloakUserId: string;
  displayName: string;
  email: string;
  role: string;
  allowedCompanyIds: string[];
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly oauthService = inject(OAuthService);
  private readonly http = inject(HttpClient);
  private readonly tableState = inject(TableStateService);
  private readonly router = inject(Router);

  readonly initializing = signal(true);

  // Заполняется в initKeycloak() из JWT после валидации Keycloak'ом.
  // Из localStorage НЕ читаем — иначе при просроченном/отозванном токене
  // UI рендерил бы юзера как залогиненного до завершения initKeycloak.
  readonly currentUser = signal<CurrentUser | null>(null);

  /** Текущий пользователь — admin-роль. Используется для гейтинга UI в ЛК (ТЗ 3.1). */
  readonly isAdmin = computed(() => this.currentUser()?.role === 'admin');

  constructor() {
    // Multi-tab sync: вкладка А логаутится → пишет маркер → все остальные
    // вкладки получают `storage`-event и форсят свой logout без ожидания 401.
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (e) => {
        if (e.key === STORAGE_KEYS.logoutBroadcast && this.currentUser()) {
          this.localCleanup();
          window.location.reload();
        }
      });
    }
  }

  async initKeycloak(): Promise<void> {
    this.oauthService.configure(environment.keycloak);
    this.oauthService.setupAutomaticSilentRefresh();

    await this.oauthService.loadDiscoveryDocumentAndTryLogin();

    if (this.oauthService.hasValidAccessToken()) {
      const user = this.extractUserFromToken();
      if (user) {
        const enriched = await this.enrichUserFromIdentityApi(user);
        this.currentUser.set(enriched);
      }
      // После успешного логина — восстанавливаем intent-URL.
      this.consumePostLoginRedirect();
    }
    this.initializing.set(false);
  }

  private consumePostLoginRedirect(): void {
    let target: string | null = null;
    try {
      target = sessionStorage.getItem(STORAGE_KEYS.postLoginRedirect);
      if (target) sessionStorage.removeItem(STORAGE_KEYS.postLoginRedirect);
    } catch {}
    if (target && target.startsWith('/')) {
      void this.router.navigateByUrl(target);
    }
  }

  login(): void {
    this.tableState.resetFiltersAndSort();
    this.oauthService.initLoginFlow();
  }

  logout(): void {
    this.localCleanup();
    // Сигналим другим вкладкам — они тоже выйдут (см. listener в constructor).
    try {
      localStorage.setItem(STORAGE_KEYS.logoutBroadcast, String(Date.now()));
    } catch {}
    this.oauthService.logOut();
  }

  private localCleanup(): void {
    // Чистим STORAGE_KEYS.currentUser на случай старых билдов, где он писался —
    // forward-compat, чтобы у пользователя не висели stale-данные после апдейта.
    localStorage.removeItem(STORAGE_KEYS.currentUser);
    sessionStorage.removeItem(STORAGE_KEYS.postLoginRedirect);
    this.tableState.resetFiltersAndSort();
    this.currentUser.set(null);
  }

  getToken(): string | null {
    return this.oauthService.getAccessToken() ?? null;
  }

  isAuthenticated(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  /**
   * Пытается обновить токен через refresh-flow. Возвращает true при успехе
   * (доступен валидный access-token), false иначе. Используется auth-interceptor
   * перед logout на 401, чтобы не выкидывать пользователя при только что
   * истёкшем токене.
   */
  async tryRefresh(): Promise<boolean> {
    try {
      await this.oauthService.refreshToken();
      return this.oauthService.hasValidAccessToken();
    } catch {
      return false;
    }
  }

  private extractUserFromToken(): CurrentUser | null {
    const claims = this.oauthService.getIdentityClaims() as Record<string, unknown> | null;
    if (!claims) return null;

    const accessToken = this.oauthService.getAccessToken();
    const role = this.extractRoleFromAccessToken(accessToken);
    const allowedCompanyIds = this.extractAllowedCompaniesFromToken(accessToken, claims);

    return {
      keycloakUserId: (claims['sub'] as string) ?? '',
      displayName: (claims['name'] as string) ?? (claims['preferred_username'] as string) ?? null,
      email: (claims['email'] as string) ?? null,
      role,
      allowedCompanyIds,
    };
  }

  /** `GET /identity/me` — список разрешённых ЮЛ из БД (не из JWT). */
  private async enrichUserFromIdentityApi(base: CurrentUser): Promise<CurrentUser> {
    try {
      const me = await firstValueFrom(
        this.http.get<BackendMeResponse>(`${environment.apiUrl}/identity/me`),
      );
      const roleRaw = (me.role ?? '').toLowerCase();
      const role: UserRole | null =
        roleRaw === 'admin' ? 'admin' : roleRaw === 'user' ? 'user' : base.role;
      const allowedCompanyIds: string[] | null =
        role === 'admin' ? null : (me.allowedCompanyIds ?? []).map(String);

      return {
        ...base,
        displayName: me.displayName?.trim() || base.displayName,
        email: me.email?.trim() || base.email,
        role,
        allowedCompanyIds,
      };
    } catch {
      return base;
    }
  }

  // Fallback, если `/identity/me` недоступен — null = «не фильтровать на клиенте».
  private extractAllowedCompaniesFromToken(
    accessToken: string | null,
    idClaims: Record<string, unknown>,
  ): string[] | null {
    const sources: (Record<string, unknown> | null)[] = [idClaims, decodeJwtPayload(accessToken)];
    for (const src of sources) {
      if (!src) continue;
      for (const key of ALLOWED_COMPANIES_CLAIM_KEYS) {
        const v = src[key];
        if (Array.isArray(v) && v.every((x) => typeof x === 'string')) {
          return v as string[];
        }
      }
    }
    return null;
  }

  // Роли в realm `vasta` заданы на уровне realm (`realm_access.roles`),
  // а не под клиентом `inventory-ui` — см. keycloak/realm-export.json.
  private extractRoleFromAccessToken(token: string | null): UserRole | null {
    const payload = decodeJwtPayload(token);
    if (!payload) return null;

    const realmAccess = payload['realm_access'] as { roles?: string[] } | undefined;
    const resourceAccess = payload['resource_access'] as Record<string, unknown> | undefined;
    const clientAccess = resourceAccess?.['inventory-ui'] as { roles?: string[] } | undefined;
    const roles = [...(realmAccess?.roles ?? []), ...(clientAccess?.roles ?? [])];

    if (roles.includes('admin')) return 'admin';
    if (roles.includes('user')) return 'user';
    return null;
  }
}
